import * as z from 'zod';

import { TFunction } from 'i18next';
import { countPasswordCategories, getPasswordPolicy, REGEX } from '@repo/utils';
import { VALIDATION_ERRORS } from '@repo/utils/constants/validation-errors-keys';
import { runtimeConfig } from '@/lib/runtime-config';

// Custom zod validators

type CustomStringErrors =
  | 'required'
  | 'invalidEmail'
  | 'min'
  | 'max'
  | 'exactLength'
  | 'invalidFormat';

interface CustomStringOptions {
  min: number;
  max: number;
  exactLength: number | null;
  regex: RegExp | null;
  errors: Partial<{
    [K in CustomStringErrors]: string;
  }>;
}

type CustomStringValidator = (
  type: 'string' | 'email',
  t: TFunction<'translation', undefined>,
  options?: Partial<CustomStringOptions>
) => z.ZodString | z.ZodEmail;

const defaultOptions = {
  min: 1,
  max: 255,
  exactLength: null,
  regex: null,
  errors: {
    required: VALIDATION_ERRORS.FIELD_REQUIRED,
    invalidEmail: VALIDATION_ERRORS.INVALID_EMAIL,
    min: VALIDATION_ERRORS.STRINGS.MIN_LENGTH,
    max: VALIDATION_ERRORS.STRINGS.MAX_LENGTH,
    exactLength: VALIDATION_ERRORS.STRINGS.EXACT_LENGTH,
    invalidFormat: VALIDATION_ERRORS.STRINGS.REGEX_MISMATCH,
  },
} satisfies CustomStringOptions;

export const customString: CustomStringValidator = (type, t, options) => {
  const totalOptions = {
    ...defaultOptions,
    ...options,
    errors: {
      ...defaultOptions.errors,
      ...options?.errors,
    },
  };
  const { min, max, exactLength, regex, errors } = totalOptions;

  let validatorsChain =
    type === 'string'
      ? z.string({ error: t(errors.required) })
      : z.email({ error: t(errors.invalidEmail) });

  // Trimming prevents that a string with only whitespaces pass the validation
  validatorsChain = validatorsChain.trim();

  // Regex validation
  if (regex)
    validatorsChain = validatorsChain.regex(regex, {
      error: t(errors.invalidFormat),
    });

  // If the string has an exact length, it's used the length() validator,
  // otherwise min-max are used.
  if (exactLength)
    validatorsChain = validatorsChain.length(exactLength, {
      error: t(errors.exactLength, { length: exactLength }),
    });

  // Min validation
  const minError = min > 1 ? t(errors.min, { min }) : t(errors.required);
  validatorsChain = validatorsChain.min(min, {
    error: minError,
  });

  // Max validation
  validatorsChain = validatorsChain.max(max, {
    error: t(errors.max, { max }),
  });

  return validatorsChain;
};

export const password = (t: TFunction<'translation', undefined>) => {
  return customString('string', t, { min: 8 });
};

export const newPassword = (t: TFunction<'translation', undefined>) => {
  const policy = getPasswordPolicy(runtimeConfig.VITE_ALLOW_WEAK_PASSWORDS);

  return z
    .string({ error: t(VALIDATION_ERRORS.FIELD_REQUIRED) })
    .trim()
    .min(1, { error: t(VALIDATION_ERRORS.FIELD_REQUIRED) })
    .superRefine((value, context) => {
      const errors: string[] = [];

      if (value.length < policy.minLength) {
        errors.push(
          t(VALIDATION_ERRORS.AUTH.PASSWORD_MIN_LENGTH, {
            min: policy.minLength,
          })
        );
      }

      if (
        policy.requiredCategories > 0 &&
        countPasswordCategories(value) < policy.requiredCategories
      ) {
        errors.push(
          t(VALIDATION_ERRORS.AUTH.PASSWORD_COMPLEXITY, {
            count: policy.requiredCategories,
          })
        );
      }

      if (errors.length > 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: errors.join(' e '),
        });
      }

      if (value.length > policy.maxLength) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(VALIDATION_ERRORS.STRINGS.MAX_LENGTH, {
            max: policy.maxLength,
          }),
        });
      }
    });
};

export const italianVat = (t: TFunction<'translation', undefined>) => {
  return customString('string', t, {
    exactLength: 11,
    regex: REGEX.VAT,
    errors: {
      invalidFormat: VALIDATION_ERRORS.INVALID_VAT_NUMBER,
    },
  });
};

export const foreignVat = (t: TFunction<'translation', undefined>) => {
  return customString('string', t, {
    max: 28,
    regex: REGEX.FOREIGN_VAT_ID,
    errors: {
      invalidFormat: VALIDATION_ERRORS.INVALID_VAT_NUMBER,
    },
  });
};

export const vat = (t: TFunction<'translation', undefined>) => {
  return z.union([italianVat(t), foreignVat(t)]);
};

export const fiscalCode = (t: TFunction<'translation', undefined>) => {
  return customString('string', t, {
    regex: REGEX.FISCAL_CODE,
    errors: {
      invalidFormat: VALIDATION_ERRORS.INVALID_FISCAL_CODE,
    },
  });
};

export const fiscalCodeOrVat = (t: TFunction<'translation', undefined>) => {
  return customString('string', t, {
    regex: REGEX.FISCAL_CODE_OR_VAT,
    errors: {
      invalidFormat: VALIDATION_ERRORS.INVALID_FISCAL_CODE,
    },
  });
};

/**
 * Optional fiscal code validator.
 * Accepts valid fiscal code, empty string, null, or undefined.
 */
export const optionalFiscalCode = (t: TFunction<'translation', undefined>) => {
  return fiscalCode(t).or(z.literal('')).nullish();
};

export const iban = (t: TFunction<'translation', undefined>) => {
  // IBAN structure: 2 letters (country) + 2 digits (check) + 11-30 alphanumeric (BBAN)
  // overall length between 15 and 34 characters
  return customString('string', t, {
    min: 15,
    max: 34,
    regex: REGEX.IBAN,
    errors: {
      invalidFormat: VALIDATION_ERRORS.INVALID_IBAN,
    },
  });
};

export const bic = (t: TFunction<'translation', undefined>) => {
  // BIC/SWIFT structure:
  // 4 letters (bank code) +
  // 2 letters (country code) +
  // 2 alphanumeric (location code) +
  // optional 3 alphanumeric (branch code)
  // total length: 8 or 11 characters
  return customString('string', t, {
    min: 8,
    max: 11,
    regex: REGEX.BIC,
    errors: {
      invalidFormat: VALIDATION_ERRORS.INVALID_BIC,
    },
  });
};

export const dateRange = z.object({
  from: z.date().nullish(),
  to: z.date().nullish(),
});

export const website = (t: TFunction<'translation', undefined>) => {
  return customString('string', t, {
    regex: REGEX.WEBSITE,
    errors: {
      invalidFormat: VALIDATION_ERRORS.INVALID_WEBSITE,
    },
  });
};

export const customEnum = <TEnum extends Record<string, string>>(
  t: TFunction<'translation', undefined>,
  values: TEnum
) => {
  return z.enum(values, {
    error: t(VALIDATION_ERRORS.CHOOSE_AMONG),
  });
};
export default z;

/**
 * Numero obbligatorio di `InputForm type="number"|"percent"`, che svuotato
 * scrive `null`: vuoto → «campo obbligatorio», poi i vincoli del campo
 * (`constrain`, es. minimo e massimo) con i loro messaggi.
 */
export const requiredNullableNumber = (
  t: TFunction,
  constrain: (schema: z.ZodNumber) => z.ZodNumber = (schema) => schema
) =>
  constrain(z.number({ message: t(VALIDATION_ERRORS.FIELD_REQUIRED) }))
    .nullable()
    .refine((value) => value !== null, {
      message: t(VALIDATION_ERRORS.FIELD_REQUIRED),
    });

/** Importo obbligatorio da zero in su (`InputForm` svuotato scrive `null`). */
export const requiredNonNegativeNumber = (t: TFunction) =>
  requiredNullableNumber(t, (schema) =>
    schema.min(0, {
      message: t(VALIDATION_ERRORS.NUMBERS.MIN_VALUE, { min: 0 }),
    })
  );

/** Intero obbligatorio da `min` (e fino a `max`, se c'è). */
export const requiredIntegerInRange = (
  t: TFunction,
  { min, max }: { min: number; max?: number }
) =>
  requiredNullableNumber(t, (schema) => {
    const bounded = schema
      .int({ message: t(VALIDATION_ERRORS.NUMBERS.MUST_BE_INTEGER) })
      .min(min, { message: t(VALIDATION_ERRORS.NUMBERS.MIN_VALUE, { min }) });
    return max === undefined
      ? bounded
      : bounded.max(max, {
          message: t(VALIDATION_ERRORS.NUMBERS.MAX_VALUE, { max }),
        });
  });

/**
 * Testo libero da `TextAreaForm` (scrive `null` quando si svuota il campo):
 * l'output è sempre una stringa e il trim viene prima dei controlli, così un
 * testo di soli spazi è un testo vuoto, come per il backend. I messaggi sono
 * chiavi i18n: le traduce `FormLabel`.
 */
const normalizedTextSchema = z
  .string({ error: VALIDATION_ERRORS.FIELD_REQUIRED })
  .nullish()
  .transform((value) => value?.trim() ?? '');

/** Testo obbligatorio, al più `max` caratteri (`tooLongKey` oltre). */
export const requiredTrimmedText = (max: number, tooLongKey: string) =>
  normalizedTextSchema.pipe(
    z
      .string()
      .min(1, { error: VALIDATION_ERRORS.FIELD_REQUIRED })
      .max(max, { error: tooLongKey })
  );

/** Testo facoltativo (stringa vuota = assente), al più `max` caratteri. */
export const optionalTrimmedText = (max: number, tooLongKey: string) =>
  normalizedTextSchema.pipe(z.string().max(max, { error: tooLongKey }));
